// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-nocheck
import { Injectable } from '@angular/core';
import { marked } from 'marked';
import { logger } from "@services/logger/logger.service";

/// How to paste text
/// https://web.dev/patterns/clipboard/paste-text/

interface IClipboardTextAreaControl
{
    textarea: HTMLTextAreaElement,
    remove: () => void,
}

@Injectable({
    providedIn: 'root'
})
export class ClipboardService
{

    constructor()
    {
        if (!window)
        {
            throw new Error('Clipboard API unavailable');
        }
    }

    public copyTo(text: string, preserveMarkdown = false): void
    {
        const copiedText = preserveMarkdown ? text : this.convertMarkdownToPlainText(text);
        const copiedHtml = this.convertMarkdownToHtml(text);
        const copied = this.copyToWithDeprecatedMethod(copiedText, copiedHtml);
        if (copied)
        {
            return;
        }

        this.askClipboardWritePermission().then(value =>
        {
            if (value)
            {
                this.writeToClipboard(copiedText, copiedHtml);
            }
        });
    }

    public async copyFrom(): Promise<null | string>
    {
        const permission = this.askClipboardReadPermission();
        if (permission)
        {
            try
            {
                return await navigator.clipboard.readText();
            } catch (e)
            {
                logger.error(e);
                return this.copyFromWithDeprecatedMethod();
            }
        }

        return this.copyFromWithDeprecatedMethod();
    }

    private copyToWithDeprecatedMethod(text: string, html?: string): boolean
    {
        const { remove } = this.createTempTextArea(text);
        const handleCopy = (event: ClipboardEvent) =>
        {
            if (!event.clipboardData)
            {
                return;
            }

            event.clipboardData.setData('text/plain', text);
            if (html)
            {
                event.clipboardData.setData('text/html', html);
            }
            event.preventDefault();
        };
        document.addEventListener('copy', handleCopy, { once: true });

        try
        {
            return document.execCommand('copy');
        }
        catch (err)
        {
            logger.error(`document.execCommand(copy) failed:`, err);
        }
        finally
        {
            document.removeEventListener('copy', handleCopy);
            remove();
        }

        return false;
    }

    public convertMarkdownToHtml(text: string): string
    {
        const html = marked.parse(text, { async: false });
        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        parsedDocument.querySelectorAll('script, style, template').forEach(element => element.remove());

        parsedDocument.body.querySelectorAll<HTMLElement>('*').forEach(element =>
        {
            element.style.fontFamily = '"Poppins", sans-serif';
            element.style.fontSize = '14px';
            element.style.lineHeight = '132%';
        });
        parsedDocument.body.querySelectorAll<HTMLElement>('p').forEach(element =>
        {
            element.style.margin = '0.5rem 0';
        });
        parsedDocument.body.querySelectorAll<HTMLElement>('ul, ol').forEach(element =>
        {
            element.style.marginTop = '0';
            element.style.paddingLeft = '18px';
        });
        parsedDocument.body.querySelectorAll<HTMLElement>('h1').forEach(element =>
        {
            element.style.fontSize = '20px';
        });
        parsedDocument.body.querySelectorAll<HTMLElement>('h2').forEach(element =>
        {
            element.style.fontSize = '18px';
        });
        parsedDocument.body.querySelectorAll<HTMLElement>('h3').forEach(element =>
        {
            element.style.fontSize = '16px';
        });
        parsedDocument.body.querySelectorAll<HTMLElement>('code').forEach(element =>
        {
            element.style.fontFamily = 'monospace';
        });

        return parsedDocument.body.innerHTML;
    }

    public convertMarkdownToPlainText(text: string): string
    {
        const markdown = text.replace(/^(?:\*\*)?#{1,6}\s+(.+?)(?:\*\*)?$/gm, '$1');
        const html = marked.parse(markdown, { async: false })
            .replace(/<br\s*\/?\s*>/gi, '\n')
            .replace(/<li[^>]*>/gi, '• ')
            .replace(/<\/(?:li|t[hd])>/gi, '\n')
            .replace(/<\/?(?:h[1-6]|p|div|blockquote|pre|ul|ol|table|thead|tbody|tfoot|tr|hr)[^>]*>/gi, '\n');

        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        parsedDocument.querySelectorAll('script, style, template').forEach(element => element.remove());
        const plainText = parsedDocument.body.textContent ?? '';
        return plainText.replace(/\n{3,}/g, '\n\n').trim();
    }

    private writeToClipboard(text: string, html: string): void
    {
        if (typeof ClipboardItem === 'undefined' || !navigator.clipboard.write)
        {
            navigator.clipboard.writeText(text);
            return;
        }

        const clipboardItem = new ClipboardItem({
            'text/plain': new Blob([ text ], { type: 'text/plain' }),
            'text/html': new Blob([ html ], { type: 'text/html' }),
        });
        navigator.clipboard.write([ clipboardItem ]);
    }

    private copyFromWithDeprecatedMethod(): null | string
    {
        const { remove, textarea } = this.createTempTextArea('');

        try
        {
            const res = document.execCommand('paste');
            logger.log(res);
            logger.log(textarea.value);
            return textarea.value;
        }
        catch (err)
        {
            logger.error(`document.execCommand(copy) failed:`, err);
        }
        finally
        {
            remove();
        }

        return null;
    }

    private createTempTextArea(text: string): IClipboardTextAreaControl
    {
        const textarea = document.createElement("textarea");
        textarea.value = text;

        textarea.style.border = "none";
        textarea.style.color = "white";
        textarea.style.backgroundColor = "transparent";
        textarea.style.top = "0";
        textarea.style.left = "0";
        textarea.style.position = "fixed";

        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();

        return {
            textarea,
            remove: () => document.body.removeChild(textarea),
        };
    }

    private async askClipboardWritePermission(): Promise<void>
    {
        try
        {
            const { state } = await navigator.permissions.query({ name: 'clipboard-write' });
            return state === 'granted';
        }
        catch (err)
        {
            return false;
        }
    }

    private async askClipboardReadPermission(): Promise<void>
    {
        try
        {
            const queryOpts = { name: 'clipboard-read', allowWithoutGesture: false };
            const { state } = await navigator.permissions.query(queryOpts);
            return state === 'granted';
        }
        catch (err)
        {
            return false;
        }
    }
}
